import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import camelCase from 'lodash/camelCase';
import { formatDate, formatHour12, DEFAULT_START_HOUR, DEFAULT_END_HOUR } from '@reports/presentation/utils';
import useAmplitude from 'providers/root/AmplitudeProvider/useAmplitude';
import dayjs from 'dayjs';

import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
  SheetClose,
  SheetOverlay,
  Button,
  Label,
  MultiSelect,
  Alert,
  AlertDescription,
} from '@parrot-software/design-system';
import {
  ListFilter as ListFilterIcon,
  RotateCw as RotateCwIcon,
  Info as InfoIcon,
} from '@parrot-software/design-system/icons';
import { ReportTimePicker } from '@reports/presentation/components/base/ReportTimePicker';
import { ReportSelect } from '@reports/presentation/components/base/ReportSelect';
import type { FilterOptionsViewDto } from '@reports/application/dtos';
import type { ReportDateRange } from '@reports/domain/value-objects';
import { ReportInputTypes } from '@reports/domain/types';
import { useReportsState } from '@reports/presentation/contexts/useReportsState';
import { calculateDateRangeDays } from '@reports/presentation/utils/analyticsHelpers';
import type { Report } from '@reports/domain/entities';

export interface AppliedFilters {
  startHour: string;
  endHour: string;
  filters: Record<string, ReadonlyArray<string>>;
}

interface ReportFiltersSheetProps {
  onApply: (appliedFilters: AppliedFilters) => void;
  filterOptions?: FilterOptionsViewDto;
  filtersSelected: Record<string, ReadonlyArray<string>>;
  dateRange: ReportDateRange;
  defaultStartHour?: string;
  defaultEndHour?: string;
  isFiltersLoading: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportDefinition: Report;
}

/**
 * ReportFiltersSheet - Right sheet for report filters
 *
 * Displays a right-side sheet with dynamic filter options based on report definition.
 * Includes time pickers for start and end hours, and dynamic filters.
 * All filter values are only sent when the user clicks "Apply filters".
 *
 * @param {ReportFiltersSheetProps} props - Component properties
 * @param {(appliedFilters: AppliedFilters) => void} props.onApply - Callback when filters are applied
 * @param {FilterOptionsViewDto} [props.filterOptions] - Filter options data from API
 * @param {Record<string, string[]>} props.filtersSelected - Selected filters from the report
 * @param {ReportDateRange} props.dateRange - Current date range for display
 * @param {string} [props.defaultStartHour=DEFAULT_START_HOUR] - Default start hour value
 * @param {string} [props.defaultEndHour=DEFAULT_END_HOUR] - Default end hour value
 * @param {boolean} props.isFiltersLoading - Whether the filters catalog is loading
 * @param {boolean} props.open - Controlled open state
 * @param {(open: boolean) => void} props.onOpenChange - Callback when open state changes
 * @param {Report} props.reportDefinition - Complete report definition with metadata and filters
 * @returns {React.ReactElement} Filters sheet component
 *
 * @example
 * <ReportFiltersSheet
 *   onApply={handleApplyFilters}
 *   filterOptions={filterCatalogData}
 *   filtersSelected={filtersSelected}
 *   dateRange={dateRange}
 *   defaultStartHour="12:00 AM"
 *   defaultEndHour="11:59 PM"
 *   isFiltersLoading={filterCatalogState.isFetching}
 *   reportDefinition={reportDefinition}
 * />
 */
export const ReportFiltersSheet = ({
  onApply,
  filterOptions,
  filtersSelected,
  dateRange,
  defaultStartHour = DEFAULT_START_HOUR,
  defaultEndHour = DEFAULT_END_HOUR,
  isFiltersLoading,
  open,
  onOpenChange,
  reportDefinition,
}: ReportFiltersSheetProps) => {
  const { t } = useTranslation();
  const { logEvent } = useAmplitude();
  const [startHour, setStartHour] = useState<string>(defaultStartHour);
  const [endHour, setEndHour] = useState<string>(defaultEndHour);
  const [selectedFilters, setSelectedFilters] = useState(filtersSelected);
  const { state } = useReportsState();
  const filterConfigs = reportDefinition.filters;

  // Validate that start datetime is before end datetime
  const hasTimeRangeError = useMemo(() => {
    const startDateTime = dayjs(dateRange.start)
      .hour(Number(startHour.split(':')[0]))
      .minute(Number(startHour.split(':')[1]));
    const endDateTime = dayjs(dateRange.end)
      .hour(Number(endHour.split(':')[0]))
      .minute(Number(endHour.split(':')[1]));

    return startDateTime.isSameOrAfter(endDateTime);
  }, [startHour, endHour, dateRange.start, dateRange.end]);

  const isClearFiltersDisabled =
    Object.keys(selectedFilters).length === 0 && startHour === DEFAULT_START_HOUR && endHour === DEFAULT_END_HOUR;

  useEffect(() => {
    if (open) {
      handleResetFilters();
    }
  }, [open]);

  const handleResetFilters = useCallback(() => {
    setSelectedFilters(filtersSelected);
    setStartHour(defaultStartHour);
    setEndHour(defaultEndHour);
  }, [filtersSelected, defaultStartHour, defaultEndHour]);

  const handleApply = useCallback(() => {
    const appliedFilters: AppliedFilters = {
      startHour,
      endHour,
      filters: selectedFilters,
    };
    onApply(appliedFilters);
    onOpenChange(false);

    logEvent('@event.$reports.reportFilterApplied', `@@section.${state.currentReport?.type}`, {
      filters_count: Object.values(selectedFilters).filter((value) => value.length > 0).length,
      date_range_days: calculateDateRangeDays(dateRange.start, dateRange.end),
      report_name: state.currentReport?.type || 'unknown',
    });
  }, [
    onApply,
    onOpenChange,
    startHour,
    endHour,
    selectedFilters,
    state.currentReport?.type,
    dateRange.start,
    dateRange.end,
  ]);

  const handleFilterChange = useCallback(
    (filterKey: string, value: string | string[]) => {
      setSelectedFilters((prev) => ({
        ...prev,
        [filterKey]: [...value],
      }));

      logEvent('@event.$reports.reportFilterModified', `@@section.${state.currentReport?.type}`, {
        filter_type: camelCase(filterKey),
        report_name: state.currentReport?.type || 'unknown',
      });
    },
    [state.currentReport?.type],
  );

  const handleClearFilters = useCallback(() => {
    setSelectedFilters({});
    setStartHour(DEFAULT_START_HOUR);
    setEndHour(DEFAULT_END_HOUR);
  }, []);

  const handleStartHourChange = useCallback(
    (hour: string) => {
      setStartHour(hour);

      logEvent('@event.$reports.reportFilterModified', `@@section.${state.currentReport?.type}`, {
        filter_type: 'time',
        report_name: state.currentReport?.type || 'unknown',
      });
    },
    [state.currentReport?.type],
  );

  const handleEndHourChange = useCallback(
    (hour: string) => {
      setEndHour(hour);

      logEvent('@event.$reports.reportFilterModified', `@@section.${state.currentReport?.type}`, {
        filter_type: 'time',
        report_name: state.currentReport?.type || 'unknown',
      });
    },
    [state.currentReport?.type],
  );

  return (
    <Sheet onOpenChange={onOpenChange} open={open}>
      <SheetTrigger asChild>
        <Button className="w-full md:w-auto" disabled={isFiltersLoading} variant="outline">
          <ListFilterIcon size={16} />
          {t('reportCommon:buttons.filters')}
        </Button>
      </SheetTrigger>

      <SheetOverlay className="bg-black/50 z-1300" />
      <SheetContent className="w-[385px] max-w-[385px] sm:w-[385px] sm:max-w-[385px] flex flex-col p-0 font-sans z-1350">
        <SheetHeader className="text-left p-4">
          <SheetTitle className="text-base">{t('reportCommon:labels.filters')}</SheetTitle>
          <SheetDescription>
            <Trans
              components={{ strong: <strong className="font-semibold" /> }}
              i18nKey="reportCommon:messages.descriptionFilters"
            />
          </SheetDescription>
        </SheetHeader>

        <div className="grid flex-1 auto-rows-min gap-4 px-4 overflow-y-auto grid-cols-[minmax(0,1fr)]">
          {/* Time Range Filters */}
          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-4">
              <ReportTimePicker
                error={hasTimeRangeError}
                label={t('reportCommon:labels.startHour')}
                onChange={handleStartHourChange}
                value={startHour}
              />
              <ReportTimePicker
                error={hasTimeRangeError}
                label={t('reportCommon:labels.endHour')}
                onChange={handleEndHourChange}
                value={endHour}
              />
            </div>

            {hasTimeRangeError && (
              <div className="text-sm text-destructive">
                <span>{t('reportCommon:messages.startHourBeforeEndHour')}</span>
              </div>
            )}
          </div>

          {/* Info Message */}
          <Alert className="bg-(--feedback-bg-info) border-(--feedback-info) text-(--feedback-info)">
            <InfoIcon className="text-(--feedback-info)! mt-0.5 shrink-0" size={16} />
            <AlertDescription className="font-light">
              <Trans
                components={{ strong: <strong className="font-medium" /> }}
                i18nKey={reportDefinition.metadata.filterDateRangeInfo}
                values={{
                  startHour: formatHour12(startHour),
                  startDate: formatDate(dateRange.start),
                  endHour: formatHour12(endHour),
                  endDate: formatDate(dateRange.end),
                }}
              />
            </AlertDescription>
          </Alert>

          {/* Dynamic Filters */}
          {filterConfigs.map((filterConfig, index) => {
            const options = filterOptions?.filters[filterConfig.key] || [];
            const selectedValue = selectedFilters[filterConfig.key];
            const filterId = `filter-${filterConfig.key}`;

            if (filterConfig.inputType === ReportInputTypes.MULTISELECT) {
              return (
                <div className="flex flex-col gap-2" key={`${filterConfig.key}-${index}`}>
                  <Label className="text-sm font-medium w-fit" htmlFor={filterId}>
                    {t(filterConfig.label)}
                  </Label>
                  <MultiSelect
                    clearLabel={t('reportCommon:buttons.clear')}
                    closeLabel={t('reportCommon:buttons.close')}
                    defaultValue={Array.isArray(selectedValue) ? selectedValue : []}
                    id={filterId}
                    maxCount={5}
                    modalPopover
                    moreLabel={t('reportCommon:buttons.more')}
                    onValueChange={(values) => handleFilterChange(filterConfig.key, values)}
                    options={options.map((opt) => ({
                      value: opt.key,
                      label: opt.value,
                    }))}
                    placeholder={filterConfig.placeholder ? t(filterConfig.placeholder) : undefined}
                    popoverClassName="z-1350"
                    searchPlaceholder={t('reportCommon:labels.search')}
                    searchable={filterConfig.searchable}
                    selectAllLabel={t('reportCommon:buttons.selectAll')}
                    singleLine
                  />
                </div>
              );
            }

            if (filterConfig.inputType === ReportInputTypes.SELECT) {
              return (
                <div className="flex flex-col gap-2" key={`${filterConfig.key}-${index}`}>
                  <ReportSelect
                    label={filterConfig.label}
                    onChange={(value) => handleFilterChange(filterConfig.key, value)}
                    options={options}
                    placeholder={filterConfig.placeholder}
                    value={typeof selectedValue === 'string' ? selectedValue : ''}
                  />
                </div>
              );
            }

            return null;
          })}

          {/* Clear Filters Button */}
          <Button
            className="justify-center gap-2"
            disabled={isClearFiltersDisabled}
            onClick={handleClearFilters}
            variant="outline"
          >
            <RotateCwIcon size={16} />
            {t('common:buttons.clearFilters')}
          </Button>
        </div>

        <SheetFooter className="flex flex-col gap-2 space-x-0 p-4 mt-6 sm:flex-col sm:space-x-0">
          <Button className="w-full" disabled={hasTimeRangeError} onClick={handleApply}>
            {t('reportCommon:buttons.applyFilters')}
          </Button>
          <SheetClose asChild className="w-full">
            <Button className="w-full" variant="outline">
              {t('reportCommon:buttons.cancel')}
            </Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
